import { formatVehicleIslandCenterSubline } from "../../../MapLibre/vehicleInfoChrome/vehicleInfoChrome.tools";
import { type PopupContent } from "../../../MapLibre/vehicleSelection/vehicleSelection.types";
import { type DynamicIslandVehicleRealtimePayload } from "../../dynamicIsland.types";

/**
 * Returns the sign character of a delay string (+ / − / empty).
 *
 * @param delay Delay label from RT content.
 * @returns Sign token for comparison.
 */
export function getVehicleDelaySign( delay: string ): string {
    const trimmed = delay.trim();

    if ( trimmed.startsWith( "+" ) ) {
        return "+";
    }

    if ( trimmed.startsWith( "-" ) || trimmed.startsWith( "−" ) ) {
        return "-";
    }

    return "";
}

/**
 * Stable animation session key for vehicle realtime payloads.
 *
 * @param payload Vehicle realtime island payload.
 * @returns Key that ignores RT field churn.
 */
export function getVehicleRealtimeAnimationKey( payload: DynamicIslandVehicleRealtimePayload ): string {
    return `vehicleRealtime:${ payload.tripId }`;
}

/**
 * Whether RT text fields changed between two payloads (ignores lngLat).
 *
 * @param previous Prior payload.
 * @param next Next payload.
 * @returns True when delay, headsign, or stop text changed.
 */
export function hasVehicleRealtimeTextDelta(
    previous: DynamicIslandVehicleRealtimePayload,
    next: DynamicIslandVehicleRealtimePayload,
): boolean {
    return !isSameVehicleRealtimeText( previous.content, next.content );
}

/**
 * Whether the delay sign flipped (for assertive aria-live).
 *
 * @param previous Prior payload.
 * @param next Next payload.
 * @returns True when delay sign changed.
 */
export function hasVehicleDelaySignFlip(
    previous: DynamicIslandVehicleRealtimePayload,
    next: DynamicIslandVehicleRealtimePayload,
): boolean {
    return getVehicleDelaySign( previous.content.delay ) !== getVehicleDelaySign( next.content.delay );
}

/**
 * Compares RT text fields on popup content.
 *
 * @param previous Prior content.
 * @param next Next content.
 * @returns True when text fields are equal.
 */
function isSameVehicleRealtimeText( previous: PopupContent, next: PopupContent ): boolean {
    return previous.delay === next.delay &&
      previous.delaySec === next.delaySec &&
      previous.stop_name === next.stop_name &&
      previous.arrival_time === next.arrival_time &&
      previous.trip_headsign === next.trip_headsign &&
      previous.label === next.label &&
      formatVehicleIslandCenterSubline(
          previous.route_type_france,
          previous.trip_headsign,
          previous.trip_headsign_original,
      ) === formatVehicleIslandCenterSubline(
          next.route_type_france,
          next.trip_headsign,
          next.trip_headsign_original,
      );
}
